import { useCallback, useState } from 'react';

/** Structural equality for plain form values (strings, numbers, booleans, arrays, plain objects). */
export function sameValues(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      sameValues((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}

export interface DirtyForm<T> {
  /** What the fields show. */
  values: T;
  setValues: (next: T | ((current: T) => T)) => void;
  /** The values as last loaded or saved - what Discard puts back. */
  saved: T;
  /** True while `values` differ from `saved`: the page header then offers Save and Discard. */
  dirty: boolean;
  /** Puts the last saved values back. */
  discard: () => void;
  /** After a load or a successful save: the given values (default: the current ones) become both the saved and shown values. */
  markSaved: (next?: T) => void;
}

/**
 * Design review 2 Nov 2026 ("always editable, Save when changed"): a form has no Edit step. It tracks whether it differs
 * from what was last saved, so the page header shows Save and Discard only when there is something to save.
 */
export function useDirtyForm<T>(initial: T, equals: (a: T, b: T) => boolean = sameValues): DirtyForm<T> {
  const [values, setValues] = useState<T>(initial);
  const [saved, setSaved] = useState<T>(initial);

  const discard = useCallback(() => setValues(saved), [saved]);
  const markSaved = useCallback(
    (next?: T) => {
      const target = next ?? values;
      setSaved(target);
      setValues(target);
    },
    [values],
  );

  return { values, setValues, saved, dirty: !equals(values, saved), discard, markSaved };
}
